import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-telefone-validacao',
  templateUrl: './telefone-validacao.component.html',
  styleUrls: ['./telefone-validacao.component.scss']
})
export class TelefoneValidacaoComponent implements OnInit {
  @Input() telefone: string = '';
  @Input() codigoJaEnviado: boolean = false;
  @Output() telefoneValidado = new EventEmitter<{ validado: boolean; codigo?: string }>();
  @Output() telefoneAlterado = new EventEmitter<string>();

  formEnviarCodigo: FormGroup;
  formValidarCodigo: FormGroup;
  
  codigoEnviado = false;
  enviandoCodigo = false;
  telefoneValidadoSucesso = false;
  mostrarErroValidacao = false;
  
  tempoRestante = 0;
  intervalTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private toastr: ToastrService
  ) {
    this.formEnviarCodigo = this.fb.group({
      telefone: ['', [Validators.required, Validators.pattern(/^\d{10,11}$/)]]
    });

    this.formValidarCodigo = this.fb.group({
      codigo: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  ngOnInit() {
    if (this.telefone) {
      this.formEnviarCodigo.patchValue({ telefone: this.telefone });
    }
    
    if (this.codigoJaEnviado) {
      this.codigoEnviado = true;
      this.iniciarContadorTempo();
    }
  }

  ngOnDestroy() {
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer);
    }
  }

  async enviarCodigo() {
    if (this.formEnviarCodigo.invalid) {
      this.formEnviarCodigo.markAllAsTouched();
      return;
    }

    this.enviandoCodigo = true;
    const telefone = this.formEnviarCodigo.get('telefone')?.value;
    
    if (telefone !== this.telefone) {
      this.telefoneAlterado.emit(telefone);
    }

    try {
      const response = await this.http.post<{ sucesso: boolean; mensagem: string }>(
        `${environment.API_URL_NEST}telefone-validacao/enviar-codigo`,
        { telefone }
      ).toPromise();

      if (response.sucesso) {
        this.codigoEnviado = true;
        this.iniciarContadorTempo();
        this.toastr.success(response.mensagem);
      } else {
        this.toastr.error(response.mensagem);
      }
    } catch (error: unknown) {
      this.toastr.error((error as { error?: { message?: string } })?.error?.message || 'Erro ao enviar código de validação');
    } finally {
      this.enviandoCodigo = false;
    }
  }

  continuarComCompra() {
    if (this.formValidarCodigo.invalid) {
      this.formValidarCodigo.markAllAsTouched();
      return;
    }

    this.mostrarErroValidacao = false;
    const codigo = this.formValidarCodigo.get('codigo')?.value;
    
    if (codigo && codigo.length === 6) {
      this.telefoneValidadoSucesso = true;
      this.toastr.success('Código inserido! Continuando com a compra...');
      
      const telefoneAtual = this.formEnviarCodigo.get('telefone')?.value || this.telefone;
      this.telefoneValidado.emit({ validado: true, codigo: codigo });
      
      if (telefoneAtual !== this.telefone) {
        this.telefoneAlterado.emit(telefoneAtual);
      }
      
      if (this.intervalTimer) {
        clearInterval(this.intervalTimer);
      }
    } else {
      this.toastr.error('Código deve ter 6 dígitos');
    }
  }

  iniciarContadorTempo() {
    this.tempoRestante = 600;
    
    this.intervalTimer = setInterval(() => {
      this.tempoRestante--;
      
      if (this.tempoRestante <= 0) {
        clearInterval(this.intervalTimer);
        this.codigoEnviado = false;
        this.toastr.warning('Código expirado. Solicite um novo código.');
      }
    }, 1000);
  }

  formatarTempo(segundos: number): string {
    const minutos = Math.floor(segundos / 60);
    const segs = segundos % 60;
    const minutosStr = minutos < 10 ? '0' + minutos : minutos.toString();
    const segsStr = segs < 10 ? '0' + segs : segs.toString();
    return `${minutosStr}:${segsStr}`;
  }

  reenviarCodigo() {
    this.codigoEnviado = false;
    this.formValidarCodigo.reset();
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer);
    }
    this.enviarCodigo();
  }

  getTelefoneValue(): string {
    const telefone = this.formEnviarCodigo.get('telefone')?.value || this.telefone || '';
    return this.formatarTelefone(telefone);
  }

  formatarTelefone(telefone: string): string {
    if (!telefone) return '';
    
    const numeros = telefone.replace(/\D/g, '');
    
    if (numeros.length === 11) {
      return `(${numeros.substring(0, 2)}) ${numeros.substring(2, 7)}-${numeros.substring(7)}`;
    } else if (numeros.length === 10) {
      return `(${numeros.substring(0, 2)}) ${numeros.substring(2, 6)}-${numeros.substring(6)}`;
    }
    
    return telefone;
  }

  isTelefoneInvalid(): boolean {
    const control = this.formEnviarCodigo.get('telefone');
    return !!(control?.invalid && control?.touched);
  }

  hasTelefoneError(errorType: string): boolean {
    const control = this.formEnviarCodigo.get('telefone');
    return !!(control?.errors && control.errors[errorType]);
  }

  isCodigoInvalid(): boolean {
    const control = this.formValidarCodigo.get('codigo');
    return !!(control?.invalid && control?.touched);
  }

  hasCodigoError(errorType: string): boolean {
    const control = this.formValidarCodigo.get('codigo');
    return !!(control?.errors && control.errors[errorType]);
  }

  resetarParaNovaTentativa() {
    this.telefoneValidadoSucesso = false;
    this.mostrarErroValidacao = true;
    this.formValidarCodigo.reset();
    this.formValidarCodigo.get('codigo')?.setErrors(null);
    
    setTimeout(() => {
      this.mostrarErroValidacao = false;
    }, 5000);
  }
}
